import type { ClientProfile, ClientStatus } from '../data/types'
import { apiRequest } from './auth'

type ApiEnvelope<T> = {
  data: T
  meta: {
    requestId: string
    total?: number
    nextCursor?: string | null
    statusCounts?: Record<string, number>
  }
}

export type CreateClientInput = {
  clientNumber?: string
  firstName: string
  lastName: string
  middleName?: string
  phoneHome?: string
  phoneCell?: string
  dob: string
  gender?: string
  status: ClientStatus
  email?: string
  admissionDate?: string
  address?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  ssn?: string
  careManager?: string
  program?: string
  medicaidId?: string
  medicareId?: string
  payerName?: string
  race?: string
  ethnicity?: string
  profile?: Record<string, unknown>
}

function withoutEmptyValues<T extends Record<string, unknown>>(input: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== '' && value !== undefined),
  ) as Partial<T>
}

export async function listClients(): Promise<ClientProfile[]> {
  const clients: ClientProfile[] = []
  let cursor: string | null = null
  do {
    const params = new URLSearchParams({ limit: '200' })
    if (cursor) params.set('cursor', cursor)
    const response: ApiEnvelope<ClientProfile[]> = await apiRequest(`clients?${params}`)
    clients.push(...response.data)
    cursor = response.meta.nextCursor ?? null
  } while (cursor)
  return clients
}

export async function getClient(id: string): Promise<ClientProfile> {
  const response = await apiRequest<ApiEnvelope<ClientProfile>>(`clients/${encodeURIComponent(id)}`)
  return response.data
}

export async function createClient(input: CreateClientInput): Promise<ClientProfile> {
  const response = await apiRequest<ApiEnvelope<ClientProfile>>('clients', {
    method: 'POST',
    body: JSON.stringify(withoutEmptyValues(input as Record<string, unknown>)),
  })
  return response.data
}

export async function updateClient(id: string, input: Partial<CreateClientInput> & { version?: number }): Promise<ClientProfile> {
  const response = await apiRequest<ApiEnvelope<ClientProfile>>(`clients/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(withoutEmptyValues(input as Record<string, unknown>)),
  })
  return response.data
}

export async function archiveClient(id: string): Promise<void> {
  await apiRequest(`clients/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
